import { PaymentXLSData } from 'mk2/apps/bo/containers/PaymentOrders/PaymentOrders';
import {
    PAYMENT_ORDERS_LOAD_TRIGGER,
    paymentOrdersLoad,
    PaymentOrdersLoadNormalizedResponse,
    PaymentOrdersLoadTriggerAction,
} from 'mk2/apps/bo/containers/PaymentOrders/PaymentOrders.actions';
import { normalizeError } from 'mk2/helpers/api';
import { bufferToString, readFile } from 'mk2/helpers/files';
import { YMDHMSLocal, YMDLocal } from 'mk2/helpers/timeformat';
import { getLogger } from 'mk2/logger';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import * as XLSX from 'xlsx';
import * as XML from 'xml-light';

const logger = getLogger('bo/PaymentOrders.sagas');

function* parseExcel({xlsFile}: PaymentOrdersLoadTriggerAction) {
    yield put(paymentOrdersLoad.request());

    try {
        const fileData: ArrayBuffer = yield call(() => readFile(xlsFile));

        // pre-process data
        const binary = bufferToString(fileData);

        let sheet;
        try {
            const workbook = XLSX.read(binary, {type: 'binary'});
            if (!workbook || !workbook.SheetNames || !workbook.SheetNames.length) {
                throw new Error('xls parse error');
            }

            sheet = workbook.Sheets[workbook.SheetNames[0]];
            if (!sheet) {
                throw new Error('xls parse error');
            }
        } catch (error) {
            yield put(paymentOrdersLoad.failure('Could not parse XLS file'));
            return;
        }

        if (!sheet.A1 || sheet.A1.v !== 'Meno') {
            yield put(paymentOrdersLoad.failure('Wrong content of XLS file. 1st column should be: Meno'));
            return;
        }
        if (!sheet.B1 || sheet.B1.v !== 'Platba') {
            yield put(paymentOrdersLoad.failure('Wrong content of XLS file. 2nd column should be: Platba'));
            return;
        }
        if (!sheet.C1 || sheet.C1.v !== 'IBAN') {
            yield put(paymentOrdersLoad.failure('Wrong content of XLS file. 3rd column should be: IBAN'));
            return;
        }
        if (!sheet.D1 || sheet.D1.v !== 'BIC') {
            yield put(paymentOrdersLoad.failure('Wrong content of XLS file. 4th column should be: BIC'));
            return;
        }
        if (!sheet.E1 || sheet.E1.v !== 'VS') {
            yield put(paymentOrdersLoad.failure('Wrong content of XLS file. 5th column should be: VS'));
            return;
        }
        if (!sheet.F1 || sheet.F1.v !== 'KS') {
            yield put(paymentOrdersLoad.failure('Wrong content of XLS file. 6th column should be: KS'));
            return;
        }
        if (!sheet.G1 || sheet.G1.v !== 'Info') {
            yield put(paymentOrdersLoad.failure('Wrong content of XLS file. 7th column should be: Info'));
            return;
        }

        const data = XLSX.utils.sheet_to_json(sheet);
        const outputFilename = xlsFile.name.replace(/\.xlsx$/i, '');
        const outputXML = processData(data, outputFilename);

        const normalizedResponse: PaymentOrdersLoadNormalizedResponse = {
            entities: {},
            result: {
                outputFilename,
                outputXML,
            },
        };
        yield put(paymentOrdersLoad.success(normalizedResponse));
    } catch (error) {
        logger.error(error);
        yield put(paymentOrdersLoad.failure(normalizeError(error)));
    }
}

function processData(payments: PaymentXLSData[], filename: string) {
    const date = new Date();
    const today = YMDLocal(date);
    const todayWithTime = YMDHMSLocal(date);
    let CtrlSum: number = 0;

    payments.map((payment) => {
        CtrlSum = CtrlSum + +payment.Platba;
    });

    const filenameSlug = filename.replace(/\s\s+/g, '').replace(/ /g, '-');

    const output = XML.xml(
        ['CstmrCdtTrfInitn', null,
            ['GrpHdr', null, [
                ['MsgId', null, filenameSlug],
                ['CreDtTm', null, todayWithTime],
                ['Authstn', null, [
                    ['Prtry', null, 'BATCH'],
                ]],
                ['NbOfTxs', null, payments.length],
                ['CtrlSum', null, CtrlSum.toFixed(2)],
                ['InitgPty', null, [
                    ['Nm', null, 'Modry konik s.r.o.'],
                ]],
            ]],
            ['PmtInf', null, [
                ['PmtInfId', null, filenameSlug + '-1'],
                ['PmtMtd', null, 'TRF'],
                ['PmtTpInf', null, [
                    ['InstrPrty', null, 'NORM'],
                    ['SvcLvl', null, [
                        ['Cd', null, 'SEPA'],
                    ]],
                ]],
                ['ReqdExctnDt', null, today],
            ],
            ['Dbtr', null, [
                ['Nm', null, 'Modry konik s.r.o.'],
            ]],
            ['DbtrAcct', null, [
                ['Id', null, [
                    ['IBAN', null, 'SK3011000000002624839364'],
                ]],
            ]],
            ['DbtrAgt', null, [
                ['FinInstnId', null, [
                    ['BIC', null, 'TATRSKBX'],
                ]],
            ]],
            ['ChrgBr', null, 'SLEV'],

            payments.map((payment, index) => {
                if (!!payment.Meno) {
                    return (
                        ['CdtTrfTxInf', null, [
                            ['PmtId', null, [
                                ['EndToEndId', null, filenameSlug + '-1-' + index],
                            ]],
                            ['Amt', null, [
                                ['InstdAmt', {Ccy: 'EUR'}, payment.Platba],
                            ]],
                            ['ChrgBr', null, 'SLEV'],
                            ['CdtrAgt', null, [
                                ['FinInstnId', null, [
                                    ['BIC', null, (payment.BIC || '').trim()],
                                ]],
                            ]],
                            ['Cdtr', null, [
                                ['Nm', null, (payment.Meno || '').trim()],
                                ['PstlAdr', null, [
                                    ['Ctry', null, 'SK'],
                                    ['AdrLine', null, 'Slovensko'],
                                ]],
                            ]],
                            ['CdtrAcct', null, [
                                ['Id', null, [
                                    ['IBAN', null, (payment.IBAN || '').trim()],
                                ]],
                            ]],
                            ['RmtInf', null, [
                                ['Ustrd', null, `/VS${payment.VS || ''}/SS/KS${payment.KS || ''} ${(payment.Info || '').trim()}`],
                            ]],
                        ]]
                    );
                }
            }),
        ]],
    );

    const final = '<Document xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns="urn:iso:std:iso:20022:tech:xsd:pain.001.001.03" xsi:schemaLocation="urn:iso:std:iso:20022:tech:xsd:pain.001.001.03 pain.001.001.03.xsd">' +
        output +
        '</Document>';

    return final;
}

export default function* root() {
    yield all([
        takeLatest(PAYMENT_ORDERS_LOAD_TRIGGER, parseExcel),
    ]);
}
